/**
 * Jitter for the schedule poll (MM #73).
 *
 * Every phone polls /api/lightshow/schedule/{pipe} on a fixed 2s interval, and
 * they all arm at roughly the same moment — so the requests arrive as
 * synchronised 2-second pulses rather than a smooth stream. At 200k clients
 * that is ~100k req/s hitting CloudFront in lockstep.
 *
 * It is the same shape as the participant-status burst that was already spread
 * out (see PLAYING_STATUS_JITTER_MS in config.js), just on the read path.
 *
 * Jitter is free here: nobody can perceive a poll landing 1.4s or 2.6s after
 * the last one, because the show start is driven by the synced clock in
 * waitForPlaying, not by which poll happens to observe the flip.
 */

/** Fraction of the base interval to vary by, in each direction. */
export const POLL_JITTER_RATIO = 0.4;

/**
 * A poll delay spread around `base`.
 *
 * @param {number}   base     - nominal interval in ms
 * @param {number}   [ratio]  - ±fraction, 0.4 = 60%..140% of base
 * @param {Function} [random] - injectable for tests
 * @returns {number} ms, never negative
 */
export function jitteredInterval(base, ratio = POLL_JITTER_RATIO, random = Math.random) {
  if (!Number.isFinite(base) || base <= 0) return 0;
  const spread = Math.min(Math.max(ratio, 0), 1); // a ratio > 1 would allow negative delays
  const factor = 1 + (random() * 2 - 1) * spread;
  return Math.max(0, Math.round(base * factor));
}

/**
 * Run `fn` on a jittered loop, re-scheduling after each run.
 *
 * setInterval cannot vary its delay, and re-arming after completion also stops
 * a slow response from stacking calls on top of each other.
 *
 * @returns {Function} cancel
 */
export function startJitteredPoll(fn, base, {
  ratio = POLL_JITTER_RATIO,
  random = Math.random,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
} = {}) {
  let timer = null;
  let cancelled = false;

  const schedule = () => {
    if (cancelled) return;
    timer = setTimeoutFn(async () => {
      if (cancelled) return;
      try {
        await fn();
      } finally {
        schedule();
      }
    }, jitteredInterval(base, ratio, random));
  };

  schedule();

  return () => {
    cancelled = true;
    if (timer) clearTimeoutFn(timer);
  };
}
